import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, Heart, Sparkles, BadgeCheck, MapPin, Filter } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` } });
const PLAN_LABEL = { free: 'Free', standard: 'Standard', premium: 'Premium', private_match: 'Private Match' };

const GENDER_OPTS = [['any', 'Any gender'], ['male', 'Male'], ['female', 'Female'], ['other', 'Other']];
const GOAL_OPTS = [['any', 'Any goal'], ['marriage', 'Marriage'], ['long-term', 'Long-term'], ['life-partner', 'Life Partner']];
const PLAN_OPTS = [['any', 'Any plan'], ['free', 'Free'], ['standard', 'Standard'], ['premium', 'Premium'], ['private_match', 'Private Match']];

const scoreColor = (s) => (s >= 80 ? 'bg-green-100 text-green-700' : s >= 55 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500');

const EMPTY = { q: '', gender: 'any', goal: 'any', plan: 'any', faith: '', values: '', ageMin: '', ageMax: '', verified: false, matchReady: false, locationFit: false, sort: 'compatibility' };

export const CandidateDiscovery = ({ clientId, onPropose }) => {
  const [f, setF] = useState(EMPTY);
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [proposed, setProposed] = useState({});
  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));

  const fetchCandidates = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ for_user: clientId, sort: f.sort, limit: '30' });
      if (f.q) p.set('q', f.q);
      if (f.gender !== 'any') p.set('gender', f.gender);
      if (f.goal !== 'any') p.set('relationship_goals', f.goal);
      if (f.plan !== 'any') p.set('plan', f.plan);
      if (f.faith) p.set('faith', f.faith);
      if (f.values) p.set('values', f.values);
      if (f.ageMin) p.set('age_min', f.ageMin);
      if (f.ageMax) p.set('age_max', f.ageMax);
      if (f.verified) p.set('verified', 'true');
      if (f.matchReady) p.set('match_ready', 'true');
      if (f.locationFit) p.set('location_fit', 'true');
      const res = await axios.get(`${API}/admin/candidates?${p.toString()}`, authHeaders());
      setResults(res.data.candidates);
      setTotal(res.data.total);
    } catch (e) { toast.error('Search failed'); }
    setLoading(false);
  }, [clientId, f]);

  useEffect(() => { fetchCandidates(); /* initial suggested list */ // eslint-disable-next-line
  }, [clientId]);

  const aiRank = async () => {
    if (results.length === 0) return;
    setAiLoading(true);
    try {
      const ids = results.slice(0, 10).map((c) => c.id);
      const res = await axios.post(`${API}/admin/candidates/insights`, { for_user: clientId, candidate_ids: ids }, authHeaders());
      const ins = res.data.insights || {};
      const merged = results.map((c) => ({ ...c, ai: ins[c.id] }));
      merged.sort((a, b) => ((b.ai?.ai_score ?? -1) - (a.ai?.ai_score ?? -1)) || ((b.compatibility?.score ?? 0) - (a.compatibility?.score ?? 0)));
      setResults(merged);
      toast.success('AI ranked the shortlist');
    } catch (e) { toast.error('AI ranking failed'); }
    setAiLoading(false);
  };

  const doPropose = async (c) => {
    await onPropose(c.id, `Matchmaker introduction${c.compatibility ? ` · ${c.compatibility.score}% compatible` : ''}`);
    setProposed((prev) => ({ ...prev, [c.id]: true }));
  };

  return (
    <div className="space-y-3 pt-2" data-testid="candidate-discovery">
      {/* Filters */}
      <div className="border border-rose-100 rounded-lg p-3 space-y-2 bg-rose-50/40">
        <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase"><Filter className="w-3.5 h-3.5" /> Filters</div>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input data-testid="disc-keyword" placeholder="Keyword (bio, values, interests)..." value={f.q}
              onChange={(e) => set('q', e.target.value)} className="pl-9 bg-white"
              onKeyDown={(e) => { if (e.key === 'Enter') fetchCandidates(); }} />
          </div>
          <Button data-testid="disc-search-btn" variant="outline" onClick={fetchCandidates}>Search</Button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <Select value={f.gender} onValueChange={(v) => set('gender', v)}>
            <SelectTrigger className="h-9 bg-white" data-testid="disc-gender"><SelectValue /></SelectTrigger>
            <SelectContent>{GENDER_OPTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={f.goal} onValueChange={(v) => set('goal', v)}>
            <SelectTrigger className="h-9 bg-white" data-testid="disc-goal"><SelectValue /></SelectTrigger>
            <SelectContent>{GOAL_OPTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={f.plan} onValueChange={(v) => set('plan', v)}>
            <SelectTrigger className="h-9 bg-white" data-testid="disc-plan"><SelectValue /></SelectTrigger>
            <SelectContent>{PLAN_OPTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <Input data-testid="disc-faith" placeholder="Faith" value={f.faith} onChange={(e) => set('faith', e.target.value)} className="h-9 bg-white" />
          <Input data-testid="disc-values" placeholder="Values (comma)" value={f.values} onChange={(e) => set('values', e.target.value)} className="h-9 bg-white" />
          <div className="flex gap-1">
            <Input data-testid="disc-age-min" type="number" placeholder="Age min" value={f.ageMin} onChange={(e) => set('ageMin', e.target.value)} className="h-9 bg-white" />
            <Input data-testid="disc-age-max" type="number" placeholder="max" value={f.ageMax} onChange={(e) => set('ageMax', e.target.value)} className="h-9 bg-white" />
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge data-testid="disc-verified" onClick={() => set('verified', !f.verified)}
            className={`cursor-pointer ${f.verified ? 'bg-blue-600' : 'bg-white text-gray-500 border border-gray-200'}`}>Verified only</Badge>
          <Badge data-testid="disc-matchready" onClick={() => set('matchReady', !f.matchReady)}
            className={`cursor-pointer ${f.matchReady ? 'bg-green-600' : 'bg-white text-gray-500 border border-gray-200'}`}>Match-ready only</Badge>
          <Badge data-testid="disc-locationfit" onClick={() => set('locationFit', !f.locationFit)}
            className={`cursor-pointer ${f.locationFit ? 'bg-rose-600' : 'bg-white text-gray-500 border border-gray-200'}`}>📍 Location fit</Badge>
          <Select value={f.sort} onValueChange={(v) => set('sort', v)}>
            <SelectTrigger className="h-8 w-40 bg-white ml-auto" data-testid="disc-sort"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="compatibility">Best match first</SelectItem>
              <SelectItem value="recent">Recently active</SelectItem>
            </SelectContent>
          </Select>
          <Button data-testid="disc-apply-btn" size="sm" onClick={fetchCandidates} className="bg-rose-600 hover:bg-rose-700">Apply</Button>
        </div>
      </div>

      {/* Header + AI rank */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500" data-testid="disc-count">{loading ? 'Searching…' : `${total} candidate${total === 1 ? '' : 's'}`}</p>
        <Button data-testid="disc-ai-rank" size="sm" variant="outline" onClick={aiRank} disabled={aiLoading || results.length === 0} className="text-indigo-600 border-indigo-200">
          <Sparkles className="w-4 h-4 mr-1" /> {aiLoading ? 'Analyzing…' : 'AI rank & explain'}
        </Button>
      </div>

      {/* Results */}
      <div className="space-y-2 max-h-[340px] overflow-y-auto">
        {results.length === 0 && !loading && <p className="text-sm text-gray-400 text-center py-6">No candidates match these filters.</p>}
        {results.map((c) => (
          <div key={c.id} className="border border-rose-100 rounded-lg p-3" data-testid={`disc-candidate-${c.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 min-w-0">
                {c.picture ? <img src={c.picture} alt="" className="w-11 h-11 rounded-full object-cover shrink-0" /> : <div className="w-11 h-11 rounded-full bg-rose-100 shrink-0" />}
                <div className="min-w-0">
                  <p className="font-medium text-sm text-rose-900 flex items-center gap-1">
                    {c.name}{c.age ? `, ${c.age}` : ''}
                    {c.is_verified && <BadgeCheck className="w-4 h-4 text-blue-500" />}
                  </p>
                  <p className="text-xs text-gray-400 flex items-center gap-1">
                    {(c.city || c.country) && <><MapPin className="w-3 h-3" /> {[c.city, c.country].filter(Boolean).join(', ')} · </>}
                    {PLAN_LABEL[c.plan_type]}
                  </p>
                  {c.compatibility?.shared_values?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {c.compatibility.shared_values.slice(0, 4).map((v) => <Badge key={v} variant="outline" className="text-[10px] py-0">{v}</Badge>)}
                    </div>
                  )}
                  {c.compatibility?.reasons?.length > 0 && (
                    <p className="text-[11px] text-gray-500 mt-1">{c.compatibility.reasons.slice(0, 2).join(' · ')}</p>
                  )}
                  {c.ai?.why && <p className="text-[11px] text-indigo-600 italic mt-1" data-testid={`disc-why-${c.id}`}>“{c.ai.why}”</p>}
                </div>
              </div>
              <div className="flex flex-col items-end gap-2 shrink-0">
                {c.ai?.ai_score != null
                  ? <Badge className={scoreColor(c.ai.ai_score)} data-testid={`disc-aiscore-${c.id}`}>AI {c.ai.ai_score}%</Badge>
                  : c.compatibility && <Badge className={scoreColor(c.compatibility.score)} data-testid={`disc-score-${c.id}`}>{c.compatibility.score}%</Badge>}
                <Button size="sm" disabled={proposed[c.id]} data-testid={`disc-propose-${c.id}`}
                  className="bg-indigo-600 hover:bg-indigo-700" onClick={() => doPropose(c)}>
                  <Heart className="w-4 h-4 mr-1" /> {proposed[c.id] ? 'Proposed' : 'Propose'}
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CandidateDiscovery;
